import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, API_BASE_URL } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  JSA,
  JSAArchiveRecord,
  JSAArchiveRecordType,
  LegacyJSADocument,
  SafeWorkPlan,
  RescuePlan,
  ToolboxTalk,
  ToolboxTalkTemplate,
  NearMiss,
  SafetyTemplate,
  IncidentReport,
  RescueDrill,
  SafetyDiagram,
  SafetyMetrics,
  ToolboxAttendanceAnalytics,
  PaginatedResponse,
  CreateJSAPayload,
  CreateSafeWorkPlanPayload,
  CreateRescuePlanPayload,
  CreateToolboxTalkPayload,
  CreateToolboxTalkTemplatePayload,
  CreateNearMissPayload,
  CreateSafetyTemplatePayload,
  CreateIncidentReportPayload,
  CreateRescueDrillPayload,
  SignaturePayload,
  ExpectedSigner,
  SignerDispositionEntry,
  ReDispositionPayload,
  GuestSigner,
  GuestJSAData,
  JSAStatus,
  SafeWorkPlanStatus,
  RescuePlanStatus,
  PlanInitialInput,
  NearMissStatus,
  NearMissSeverity,
  IncidentStatus,
  IncidentSeverity,
  IncidentCategory,
  DrillOutcome,
} from '@/lib/types';

const SAFETY_API = '/api/v1/safety';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface JSAFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: JSAStatus | '';
  project?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export interface SafeWorkPlanFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: SafeWorkPlanStatus | '';
  project?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export interface RescuePlanFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: RescuePlanStatus | '';
  status__in?: string;
  linkable_to_jsa?: 'true' | '';
  jsa__isnull?: 'true' | 'false' | '';
  project?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export interface ToolboxTalkFilters {
  page?: number;
  page_size?: number;
  q?: string;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export interface ToolboxTalkTemplateFilters {
  page?: number;
  page_size?: number;
  q?: string;
  project?: number;
  is_active?: boolean;
  ordering?: string;
}

export interface NearMissFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: NearMissStatus | '';
  severity_potential?: NearMissSeverity | '';
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export interface SafetyTemplateFilters {
  page?: number;
  page_size?: number;
  q?: string;
  template_type?: string;
  is_active?: boolean;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── JSA Hooks ──────────────────────────────────────────────────────────────

export function useJSAs(filters: JSAFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.jsas.list(clean),
    queryFn: async (): Promise<PaginatedResponse<JSA>> => {
      const response = await apiClient.get<PaginatedResponse<JSA>>(`${SAFETY_API}/jsas/`, {
        params: clean,
      });
      return response.data;
    },
    enabled,
  });
}

export function useJSA(id: number) {
  return useQuery({
    queryKey: queryKeys.jsas.detail(id),
    refetchOnWindowFocus: 'always',
    queryFn: async (): Promise<JSA> => {
      const response = await apiClient.get<JSA>(`${SAFETY_API}/jsas/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateJSAPayload): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useCreateJSAFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateJSAPayload & { template_id: number }): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/create-from-template/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useUpdateJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateJSAPayload> }): Promise<JSA> => {
      const response = await apiClient.patch<JSA>(`${SAFETY_API}/jsas/${id}/`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.jsas.detail(variables.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
    },
  });
}

export function useSignJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SignaturePayload }): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/${id}/sign/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(variables.id) });
      qc.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(variables.id), 'expected-signers'] });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
    },
  });
}

export function useAcknowledgeSignJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, acknowledgement_data, plan_initials }: { id: number; acknowledgement_data?: { hazard_id: number; viewed_at: string }[]; plan_initials?: PlanInitialInput[] }): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/${id}/sign/`, {
        acknowledgement_data,
        plan_initials,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(variables.id) });
      qc.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(variables.id), 'expected-signers'] });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
    },
  });
}

export function useExpectedSigners(jsaId: number) {
  return useQuery({
    queryKey: [...queryKeys.jsas.detail(jsaId), 'expected-signers'],
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      const response = await apiClient.get<ExpectedSigner[]>(`${SAFETY_API}/jsas/${jsaId}/expected-signers/`);
      return response.data;
    },
    enabled: jsaId > 0,
  });
}

export interface SubmitJSAPayload {
  id: number;
  override_rescue_plan?: boolean;
  override_reason?: string;
  signer_dispositions?: SignerDispositionEntry[];
}

export interface RescuePlanWarningResponse {
  rescue_plan_warning: true;
  message: string;
}

export function useSubmitJSAForSignatures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SubmitJSAPayload): Promise<JSA> => {
      const { id, ...body } = payload;
      const response = await apiClient.post<JSA>(
        `${SAFETY_API}/jsas/${id}/submit-for-signatures/`,
        body,
      );
      return response.data;
    },
    onSuccess: (data, payload) => {
      qc.setQueryData(queryKeys.jsas.detail(payload.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(payload.id), 'expected-signers'],
      });
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(payload.id), 'guest-signers'],
      });
    },
  });
}

export function useReDispositionSigner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ReDispositionPayload): Promise<JSA> => {
      const { id, ...body } = payload;
      const response = await apiClient.post<JSA>(
        `${SAFETY_API}/jsas/${id}/re-disposition-signer/`,
        body,
      );
      return response.data;
    },
    onSuccess: (data, payload) => {
      qc.setQueryData(queryKeys.jsas.detail(payload.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(payload.id), 'expected-signers'],
      });
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(payload.id), 'guest-signers'],
      });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useCloseJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/${id}/close/`);
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.jsas.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useActivateJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JSA> => {
      const response = await apiClient.post<JSA>(`${SAFETY_API}/jsas/${id}/activate/`);
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.jsas.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export interface ResolveJSARevalidationPayload {
  id: number;
  note?: string;
  signer_dispositions?: SignerDispositionEntry[];
}

export function useReaffirmJSARevalidation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      note,
      signer_dispositions,
    }: ResolveJSARevalidationPayload): Promise<JSA> => {
      const response = await apiClient.post<JSA>(
        `${SAFETY_API}/jsas/${id}/reaffirm-revalidation/`,
        { note, signer_dispositions },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.jsas.detail(variables.id), data);
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(variables.id), 'expected-signers'],
      });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useAddDispatchedCrewToJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JSA> => {
      const response = await apiClient.post<JSA>(
        `${SAFETY_API}/jsas/${id}/add-dispatched-crew/`,
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.jsas.detail(id), data);
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(id), 'expected-signers'],
      });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useRequireJSAResign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      note,
      signer_dispositions,
    }: ResolveJSARevalidationPayload): Promise<JSA> => {
      const response = await apiClient.post<JSA>(
        `${SAFETY_API}/jsas/${id}/require-resign/`,
        { note, signer_dispositions },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.jsas.detail(variables.id), data);
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(variables.id), 'expected-signers'],
      });
      qc.invalidateQueries({
        queryKey: [...queryKeys.jsas.detail(variables.id), 'guest-signers'],
      });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useDeleteJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${SAFETY_API}/jsas/${id}/`);
    },
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: queryKeys.jsas.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useDuplicateJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JSA & { pre_populated_crew: ExpectedSigner[] }> => {
      const response = await apiClient.post<JSA & { pre_populated_crew: ExpectedSigner[] }>(
        `${SAFETY_API}/jsas/${id}/duplicate/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useAddExpectedSigner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jsaId, techId }: { jsaId: number; techId: number }): Promise<ExpectedSigner> => {
      const response = await apiClient.post<ExpectedSigner>(
        `${SAFETY_API}/jsas/${jsaId}/add-expected-signer/`,
        { tech_id: techId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(variables.jsaId) });
      qc.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(variables.jsaId), 'expected-signers'] });
    },
  });
}

export function useRemoveExpectedSigner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jsaId, techId }: { jsaId: number; techId: number }): Promise<{ detail: string; tech_id: number }> => {
      const response = await apiClient.post<{ detail: string; tech_id: number }>(
        `${SAFETY_API}/jsas/${jsaId}/remove-expected-signer/`,
        { tech_id: techId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(variables.jsaId) });
      qc.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(variables.jsaId), 'expected-signers'] });
    },
  });
}

export interface QRCodeResponse {
  qr_url: string;
  qr_data_url: string;
  jsa_title: string;
  signing_progress: string;
}

export function useJSASigningQR(jsaId: number, enabled = false) {
  return useQuery({
    queryKey: [...queryKeys.jsas.detail(jsaId), 'signing-qr'],
    queryFn: async (): Promise<QRCodeResponse> => {
      const response = await apiClient.get<QRCodeResponse>(
        `${SAFETY_API}/jsas/${jsaId}/signing-qr-code/`,
        { params: { format: 'json' } }
      );
      return response.data;
    },
    enabled: jsaId > 0 && enabled,
  });
}

export function useGuestSigners(jsaId: number) {
  return useQuery({
    queryKey: [...queryKeys.jsas.detail(jsaId), 'guest-signers'],
    refetchOnWindowFocus: 'always',
    queryFn: async (): Promise<GuestSigner[]> => {
      const response = await apiClient.get<GuestSigner[]>(
        `${SAFETY_API}/jsas/${jsaId}/guest-signers/`
      );
      return response.data;
    },
    enabled: jsaId > 0,
  });
}

export function useAddGuestSigner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jsaId,
      email,
      name,
      phone,
    }: {
      jsaId: number;
      email: string;
      name?: string;
      phone?: string;
    }): Promise<GuestSigner> => {
      const response = await apiClient.post<GuestSigner>(
        `${SAFETY_API}/jsas/${jsaId}/add-guest-signer/`,
        { email, name, phone }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(variables.jsaId), 'guest-signers'] });
    },
  });
}

// ─── Guest Sign Public Hooks (no auth) ─────────────────────────────────────

import axios from 'axios';

const publicClient = axios.create({ baseURL: API_BASE_URL });

/**
 * Guest access requires proving ownership of the invited email address, except
 * for in-person kiosk links, which the server answers without an email.
 */
export function useGuestJSA(token: string, email: string) {
  return useQuery({
    queryKey: ['guest-jsa', token, email],
    queryFn: async (): Promise<GuestJSAData> => {
      const response = await publicClient.get<GuestJSAData>(
        `/api/v1/safety/guest-sign/${token}/`,
        email ? { headers: { 'X-Guest-Email': encodeURIComponent(email) } } : undefined
      );
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useSubmitGuestSign(token: string) {
  return useMutation({
    mutationFn: async ({ email, name, phone, signature_data, acknowledgement_data, plan_initials }: { email: string; name: string; phone?: string; signature_data: string; acknowledgement_data?: { hazard_id: number; viewed_at: string }[]; plan_initials?: PlanInitialInput[] }): Promise<{ detail: string; signed_at: string }> => {
      const response = await publicClient.post<{ detail: string; signed_at: string }>(
        `/api/v1/safety/guest-sign/${token}/`,
        { email, name, phone, signature_data, acknowledgement_data, plan_initials }
      );
      return response.data;
    },
  });
}

// ─── Safe Work Plan Hooks ───────────────────────────────────────────────────

export function useSafeWorkPlans(filters: SafeWorkPlanFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.safeWorkPlans.list(clean),
    enabled,
    queryFn: async (): Promise<PaginatedResponse<SafeWorkPlan>> => {
      const response = await apiClient.get<PaginatedResponse<SafeWorkPlan>>(
        `${SAFETY_API}/work-plans/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useSafeWorkPlan(id: number) {
  return useQuery({
    queryKey: queryKeys.safeWorkPlans.detail(id),
    queryFn: async (): Promise<SafeWorkPlan> => {
      const response = await apiClient.get<SafeWorkPlan>(`${SAFETY_API}/work-plans/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSafeWorkPlanPayload): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(`${SAFETY_API}/work-plans/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
    },
  });
}

export function useCreateAndApproveSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSafeWorkPlanPayload): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/create-and-approve/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
    },
  });
}

export function useUpdateSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateSafeWorkPlanPayload>;
    }): Promise<SafeWorkPlan> => {
      const response = await apiClient.patch<SafeWorkPlan>(`${SAFETY_API}/work-plans/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.detail(variables.id) });
    },
  });
}

export function useDeleteSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${SAFETY_API}/work-plans/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.all });
    },
  });
}

export function useSignSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SignaturePayload }): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/${id}/sign/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.detail(variables.id) });
    },
  });
}

export function useSubmitSafeWorkPlanForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/${id}/submit-for-review/`
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.safeWorkPlans.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useApproveSafeWorkPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/${id}/approve/`
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.safeWorkPlans.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useReturnSafeWorkPlanForChanges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/${id}/return-for-changes/`,
        { reason },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.safeWorkPlans.detail(variables.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

function useSafeWorkPlanStatusAction(
  action: 'activate' | 'close' | 'archive',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<SafeWorkPlan> => {
      const response = await apiClient.post<SafeWorkPlan>(
        `${SAFETY_API}/work-plans/${id}/${action}/`,
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.safeWorkPlans.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.safeWorkPlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useActivateSafeWorkPlan() {
  return useSafeWorkPlanStatusAction('activate');
}

export function useCloseSafeWorkPlan() {
  return useSafeWorkPlanStatusAction('close');
}

export function useArchiveSafeWorkPlan() {
  return useSafeWorkPlanStatusAction('archive');
}

// ─── Rescue Plan Hooks ──────────────────────────────────────────────────────

export function useRescuePlans(filters: RescuePlanFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.rescuePlans.list(clean),
    queryFn: async (): Promise<PaginatedResponse<RescuePlan>> => {
      const response = await apiClient.get<PaginatedResponse<RescuePlan>>(
        `${SAFETY_API}/rescue-plans/`,
        { params: clean }
      );
      return {
        ...response.data,
        results: response.data.results.map((plan) => ({
          ...plan,
          linked_jsa_ids: Array.isArray(plan.linked_jsa_ids)
            ? plan.linked_jsa_ids
            : [],
        })),
      };
    },
    enabled,
  });
}

export function useRescuePlan(id: number) {
  return useQuery({
    queryKey: queryKeys.rescuePlans.detail(id),
    queryFn: async (): Promise<RescuePlan> => {
      const response = await apiClient.get<RescuePlan>(`${SAFETY_API}/rescue-plans/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateRescuePlanPayload): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(`${SAFETY_API}/rescue-plans/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
    },
  });
}

export function useCreateAndApproveRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateRescuePlanPayload): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/create-and-approve/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useUpdateRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateRescuePlanPayload>;
    }): Promise<RescuePlan> => {
      const response = await apiClient.patch<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useDeleteRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${SAFETY_API}/rescue-plans/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
    },
  });
}

export function useSignRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SignaturePayload }): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/sign/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.detail(variables.id) });
    },
  });
}

export function useSubmitRescuePlanForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/submit-for-review/`,
        {},
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.rescuePlans.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useApproveRescuePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/approve/`
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.rescuePlans.detail(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useReturnRescuePlanForChanges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/return-for-changes/`,
        { reason },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.rescuePlans.detail(variables.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useRevertRescuePlanToDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${id}/revert-to-draft/`
      );
      return response.data;
    },
    onSuccess: (data, id) => {
      qc.setQueryData(queryKeys.rescuePlans.detail(data.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

export function useLinkRescuePlanToJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rescuePlanId, jsaId }: { rescuePlanId: number; jsaId: number }): Promise<RescuePlan> => {
      const response = await apiClient.post<RescuePlan>(
        `${SAFETY_API}/rescue-plans/${rescuePlanId}/link-to-jsa/`,
        { jsa: jsaId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.detail(variables.rescuePlanId) });
      qc.invalidateQueries({ queryKey: queryKeys.rescuePlans.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.detail(variables.jsaId) });
      qc.invalidateQueries({ queryKey: queryKeys.jsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.platform() });
    },
  });
}

// ─── Toolbox Talk Hooks ─────────────────────────────────────────────────────

export function useToolboxTalks(filters: ToolboxTalkFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.toolboxTalks.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ToolboxTalk>> => {
      const response = await apiClient.get<PaginatedResponse<ToolboxTalk>>(
        `${SAFETY_API}/toolbox-talks/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useToolboxTalk(id: number) {
  return useQuery({
    queryKey: queryKeys.toolboxTalks.detail(id),
    queryFn: async (): Promise<ToolboxTalk> => {
      const response = await apiClient.get<ToolboxTalk>(`${SAFETY_API}/toolbox-talks/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateToolboxTalk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateToolboxTalkPayload): Promise<ToolboxTalk> => {
      const response = await apiClient.post<ToolboxTalk>(`${SAFETY_API}/toolbox-talks/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalks.all });
    },
  });
}

export function useToolboxTalkTemplates(filters: ToolboxTalkTemplateFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.toolboxTalkTemplates.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ToolboxTalkTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<ToolboxTalkTemplate>>(
        `${SAFETY_API}/toolbox-talk-templates/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateToolboxTalkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateToolboxTalkTemplatePayload): Promise<ToolboxTalkTemplate> => {
      const response = await apiClient.post<ToolboxTalkTemplate>(
        `${SAFETY_API}/toolbox-talk-templates/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalkTemplates.all });
    },
  });
}

export function useGenerateToolboxTalkOccurrences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { start_date: string; end_date: string };
    }): Promise<{ occurrences_created: number }> => {
      const response = await apiClient.post<{ occurrences_created: number }>(
        `${SAFETY_API}/toolbox-talk-templates/${id}/generate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalks.all });
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalkTemplates.all });
    },
  });
}

export function useMarkKitInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { attendee_id: number; signed?: boolean };
    }): Promise<ToolboxTalk> => {
      const response = await apiClient.post<ToolboxTalk>(
        `${SAFETY_API}/toolbox-talks/${id}/kit-inspection/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalks.detail(variables.id) });
    },
  });
}

export function useGuestSignToolboxTalk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { attendee_name: string; attendee_email?: string; signature_data?: string };
    }): Promise<ToolboxTalk> => {
      const response = await apiClient.post<ToolboxTalk>(
        `${SAFETY_API}/toolbox-talks/${id}/guest-sign/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.toolboxTalks.detail(variables.id) });
    },
  });
}

export interface ToolboxAttendanceAnalyticsFilters {
  date_from?: string;
  date_to?: string;
}

export function useToolboxAttendanceAnalytics(filters: ToolboxAttendanceAnalyticsFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.toolboxAnalytics.data(clean),
    queryFn: async (): Promise<ToolboxAttendanceAnalytics> => {
      const response = await apiClient.get<ToolboxAttendanceAnalytics>(
        `${SAFETY_API}/toolbox-analytics/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── Near-Miss Hooks ────────────────────────────────────────────────────────

export function useNearMisses(filters: NearMissFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.nearMisses.list(clean),
    queryFn: async (): Promise<PaginatedResponse<NearMiss>> => {
      const response = await apiClient.get<PaginatedResponse<NearMiss>>(
        `${SAFETY_API}/near-misses/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useNearMiss(id: number) {
  return useQuery({
    queryKey: queryKeys.nearMisses.detail(id),
    queryFn: async (): Promise<NearMiss> => {
      const response = await apiClient.get<NearMiss>(`${SAFETY_API}/near-misses/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateNearMissPayload): Promise<NearMiss> => {
      const response = await apiClient.post<NearMiss>(`${SAFETY_API}/near-misses/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.nearMisses.all });
    },
  });
}

export function useResolveNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { root_cause: string; corrective_actions: string };
    }): Promise<NearMiss> => {
      const response = await apiClient.post<NearMiss>(
        `${SAFETY_API}/near-misses/${id}/resolve/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.nearMisses.all });
      qc.invalidateQueries({ queryKey: queryKeys.nearMisses.detail(variables.id) });
    },
  });
}

export function useAddNearMissFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { action: string; assigned_to?: number; due_date?: string };
    }): Promise<NearMiss> => {
      const response = await apiClient.post<NearMiss>(
        `${SAFETY_API}/near-misses/${id}/add_follow_up/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.nearMisses.detail(variables.id) });
    },
  });
}

// ─── Safety Template Hooks ──────────────────────────────────────────────────

export function useSafetyTemplates(filters: SafetyTemplateFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.safetyTemplates.list(clean),
    queryFn: async (): Promise<PaginatedResponse<SafetyTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<SafetyTemplate>>(
        `${SAFETY_API}/templates/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useSafetyTemplate(id: number) {
  return useQuery({
    queryKey: queryKeys.safetyTemplates.detail(id),
    queryFn: async (): Promise<SafetyTemplate> => {
      const response = await apiClient.get<SafetyTemplate>(`${SAFETY_API}/templates/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateSafetyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSafetyTemplatePayload): Promise<SafetyTemplate> => {
      const response = await apiClient.post<SafetyTemplate>(`${SAFETY_API}/templates/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyTemplates.all });
    },
  });
}

export function useUpdateSafetyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateSafetyTemplatePayload>;
    }): Promise<SafetyTemplate> => {
      const response = await apiClient.patch<SafetyTemplate>(
        `${SAFETY_API}/templates/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyTemplates.all });
      qc.invalidateQueries({ queryKey: queryKeys.safetyTemplates.detail(variables.id) });
    },
  });
}

// ─── Incident Report Hooks ─────────────────────────────────────────────────

export interface IncidentReportFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: IncidentStatus | '';
  severity?: IncidentSeverity | '';
  category?: IncidentCategory | '';
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export function useIncidentReports(filters: IncidentReportFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.incidents.list(clean),
    queryFn: async (): Promise<PaginatedResponse<IncidentReport>> => {
      const response = await apiClient.get<PaginatedResponse<IncidentReport>>(
        `${SAFETY_API}/incidents/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useIncidentReport(id: number) {
  return useQuery({
    queryKey: queryKeys.incidents.detail(id),
    queryFn: async (): Promise<IncidentReport> => {
      const response = await apiClient.get<IncidentReport>(`${SAFETY_API}/incidents/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateIncidentReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateIncidentReportPayload): Promise<IncidentReport> => {
      const response = await apiClient.post<IncidentReport>(`${SAFETY_API}/incidents/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents.all });
    },
  });
}

export function useUpdateIncidentReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateIncidentReportPayload>;
    }): Promise<IncidentReport> => {
      const response = await apiClient.patch<IncidentReport>(
        `${SAFETY_API}/incidents/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents.all });
      qc.invalidateQueries({ queryKey: queryKeys.incidents.detail(variables.id) });
    },
  });
}

export function useInvestigateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { root_cause?: string };
    }): Promise<IncidentReport> => {
      const response = await apiClient.post<IncidentReport>(
        `${SAFETY_API}/incidents/${id}/investigate/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents.all });
      qc.invalidateQueries({ queryKey: queryKeys.incidents.detail(variables.id) });
    },
  });
}

export function useResolveIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { root_cause: string; corrective_actions: string };
    }): Promise<IncidentReport> => {
      const response = await apiClient.post<IncidentReport>(
        `${SAFETY_API}/incidents/${id}/resolve/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents.all });
      qc.invalidateQueries({ queryKey: queryKeys.incidents.detail(variables.id) });
    },
  });
}

export function useCloseIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<IncidentReport> => {
      const response = await apiClient.post<IncidentReport>(
        `${SAFETY_API}/incidents/${id}/close/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents.all });
      qc.invalidateQueries({ queryKey: queryKeys.incidents.detail(id) });
    },
  });
}

// ─── Rescue Drill Hooks ────────────────────────────────────────────────────

export interface RescueDrillFilters {
  page?: number;
  page_size?: number;
  q?: string;
  outcome?: DrillOutcome | '';
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export function useRescueDrills(filters: RescueDrillFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.rescueDrills.list(clean),
    queryFn: async (): Promise<PaginatedResponse<RescueDrill>> => {
      const response = await apiClient.get<PaginatedResponse<RescueDrill>>(
        `${SAFETY_API}/rescue-drills/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useRescueDrill(id: number) {
  return useQuery({
    queryKey: queryKeys.rescueDrills.detail(id),
    queryFn: async (): Promise<RescueDrill> => {
      const response = await apiClient.get<RescueDrill>(`${SAFETY_API}/rescue-drills/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateRescueDrill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateRescueDrillPayload): Promise<RescueDrill> => {
      const response = await apiClient.post<RescueDrill>(`${SAFETY_API}/rescue-drills/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.rescueDrills.all });
    },
  });
}

export function useUpdateRescueDrill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateRescueDrillPayload>;
    }): Promise<RescueDrill> => {
      const response = await apiClient.patch<RescueDrill>(
        `${SAFETY_API}/rescue-drills/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rescueDrills.all });
      qc.invalidateQueries({ queryKey: queryKeys.rescueDrills.detail(variables.id) });
    },
  });
}

// ─── Safety Diagram Hooks ──────────────────────────────────────────────────

export function useSafetyDiagrams(contentType: string, objectId: number) {
  return useQuery({
    queryKey: queryKeys.safetyDiagrams.list({ content_type: contentType, object_id: objectId }),
    queryFn: async (): Promise<PaginatedResponse<SafetyDiagram>> => {
      const response = await apiClient.get<PaginatedResponse<SafetyDiagram>>(
        `${SAFETY_API}/diagrams/`,
        { params: { content_type: contentType, object_id: objectId } }
      );
      return response.data;
    },
    enabled: objectId > 0,
  });
}

export function useUploadDiagram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<SafetyDiagram> => {
      const response = await apiClient.post<SafetyDiagram>(
        `${SAFETY_API}/diagrams/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyDiagrams.all });
    },
  });
}

export function useCreateCanvasDiagram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<SafetyDiagram> => {
      const response = await apiClient.post<SafetyDiagram>(
        `${SAFETY_API}/diagrams/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyDiagrams.all });
    },
  });
}

export function useSaveDiagramCanvas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: number;
      formData: FormData;
    }): Promise<SafetyDiagram> => {
      const response = await apiClient.patch<SafetyDiagram>(
        `${SAFETY_API}/diagrams/${payload.id}/`,
        payload.formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.safetyDiagrams.all });
    },
  });
}

// ─── Safety Metrics Hooks ──────────────────────────────────────────────────

export interface SafetyMetricsFilters {
  date_from?: string;
  date_to?: string;
}

export function useSafetyMetrics(filters: SafetyMetricsFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.safetyMetrics.data(clean),
    queryFn: async (): Promise<SafetyMetrics> => {
      const response = await apiClient.get<SafetyMetrics>(
        `${SAFETY_API}/metrics/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── JSA Archive Hooks ──────────────────────────────────────────────────────

export interface JSAArchiveFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: string;
  record_type?: JSAArchiveRecordType | '';
  project?: number;
  job?: number;
  location?: number;
  date_from?: string;
  date_to?: string;
}

export function useJSAArchive(filters: JSAArchiveFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.jsaArchive.list(clean),
    queryFn: async (): Promise<PaginatedResponse<JSAArchiveRecord>> => {
      const response = await apiClient.get<PaginatedResponse<JSAArchiveRecord>>(
        `${SAFETY_API}/jsa-archive/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── Legacy JSA Document Hooks ──────────────────────────────────────────────

export function useUploadLegacyJSA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<LegacyJSADocument> => {
      const response = await apiClient.post<LegacyJSADocument>(
        `${SAFETY_API}/legacy-jsas/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.legacyJsas.all });
      qc.invalidateQueries({ queryKey: queryKeys.jsaArchive.all });
    },
  });
}
